import axiosLib from "axios";

// Utils
import {
  sendGet,
  sendPost,
  sendPut,
  sendDelete,
  sendPostWithoutSignOut,
  sendGetWithoutSignOut,
  sendDeleteWithoutSignOut
} from "../baseUtils";

const host = process.env.REACT_APP_API_URL;

let axios;

export const refreshPayPalPaymentMethods = () => {
  return sendGet("/marketplace/payment_methods/refresh").then(res => {
    return res.data;
  });
};

export const getChargebeePaymentInfo = () => sendGet("/chargebee_integrations/payment_sources");

export const getStripePaymentInfo = () => sendGet("/stripe_integrations/payment_methods?account_source=new");

export const getStripeSubscriptionInfo = () => sendGet("/stripe_integrations/subscriptions/payment_methods");

export const authorizePayPalOrder = async ({ source, authorizationId }) =>
  sendPost(`/marketplace/payment_methods/${source}/authorize`, {
    authorization_id: authorizationId
  });

export const deletePaymentMethod = async methodId => sendDelete(`/marketplace/payment_methods/${methodId}`);

export const deleteStripe = ({ id }) => sendDelete(`/stripe_integrations/payment_methods/${id}`);

export const deleteCreditCard = ({ isStripe, id }) =>
  sendDelete(
    isStripe
      ? `/stripe_integrations/payment_methods/${id}?account_source=new`
      : `/chargebee_integrations/payment_sources/${id}`
  );

export const createPaymentCharge = async ({ store, planId, upgradeWithStore = false }) => {
  if (store === "wix") return sendPostWithoutSignOut(`/stores/wix/payment_subscribe/${planId}`);
  return sendGetWithoutSignOut(
    `/stores/${store}/payment_subscribe/${planId}?upgrade_with_store=${upgradeWithStore}`
  );
};

export const createSubscription = async ({ storeId, payload }) =>
  sendPostWithoutSignOut(`/stores/${storeId}/subscriptions`, payload);

export const deletePendingSubscription = async ({ storeId }) =>
  sendDeleteWithoutSignOut(`/stores/${storeId}/pending_subscription`);

export const confirmPendingSubscription = async ({ storeId }) =>
  sendPostWithoutSignOut(`/stores/${storeId}/pending_subscription/confirm`);

export const updateStripeSubscriptionPayment = async ({ token }) =>
  sendPostWithoutSignOut("/stripe_integrations/subscription/payment_methods", {
    payment_method_id: token
  });

export const getInvoices = async () => {
  const response = await sendGet("/invoices");
  return response.data;
};

export const getEInvoices = ({ EInvoiceId }) => {
  const response = sendGet(`/invoices/${EInvoiceId}`);
  return response;
};

export const getStripePaymentIntent = ({ queryType }) => {
  const queryParam = queryType === "v2" ? "?account_source=new" : "";
  return sendGet(`/stripe_integrations/subscription/intent${queryParam}`).then(res => res.data);
};

export const getStripePaymentAuthorizationStatus = async () => {
  const response = await sendGet("/subscription");
  return response.data;
};

export const putCancelStripeAuthorizationProcess = async () => {
  const response = await sendPut("/subscription", { confirmation_url: null });
  return response.data;
};

// The "Add New Store" checkout switches to the new store provisionally: the backend returns that
// store's auth token in the response instead of moving the session cookie. The token is kept in
// memory only (never in localStorage) and sent as a bearer token on the temporary-store calls
// below, so only the payment calls act as the new store while the rest of the app keeps the
// merchant's current store.
let temporaryStoreAuthToken = "";

export const setTemporaryStoreAuthToken = token => {
  temporaryStoreAuthToken = token || "";
};

export const clearTemporaryStoreAuthToken = () => {
  temporaryStoreAuthToken = "";
};

export const hasTemporaryStoreAuthToken = () => temporaryStoreAuthToken.length > 0;

// Creates a local shared instance of Axios if doesn't exist yet.
const checkTemporaryStoreAxiosInit = () => {
  if (!axios) {
    axios = axiosLib.create({
      timeout: 60000,
      withCredentials: true,
      headers: {
        common: {
          Accept: "application/json",
          "content-type": "application/json"
        }
      }
    });

    axios.interceptors.request.use(config => {
      if (temporaryStoreAuthToken) {
        config.headers.Authorization = `Bearer ${temporaryStoreAuthToken}`;
      }
      return config;
    });
  }
};

export const getStripePaymentTemporaryStoreIntent = () => {
  checkTemporaryStoreAxiosInit();
  if (hasTemporaryStoreAuthToken()) {
    return axios.get(host + "/stripe_integrations/subscription/intent", {}).then(res => res.data);
  } else {
    return null;
  }
};

export const createTemporaryStorePaymentCharge = async ({ store, planId, upgradeWithStore = false }) => {
  checkTemporaryStoreAxiosInit();
  if (hasTemporaryStoreAuthToken()) {
    return axios.get(
      host + `/stores/${store}/payment_subscribe/${planId}?upgrade_with_store=${upgradeWithStore}`,
      {}
    );
  } else {
    return false;
  }
};

export const updateStripeTemporaryStoreSubscriptionPayment = async ({ token }) => {
  checkTemporaryStoreAxiosInit();
  if (hasTemporaryStoreAuthToken()) {
    return axios.post(host + "/stripe_integrations/subscription/payment_methods", {
      payment_method_id: token
    });
  } else {
    return false;
  }
};

export const reactivateSubscription = async storeId => {
  return sendPostWithoutSignOut(`/stores/${storeId}/subscriptions/reactivate`);
};
